import { FC, useMemo, useRef, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { Button } from '@factorialco/factorial-one-react'
import { Filter } from '@factorialco/factorial-one-react/icons/app'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'

import useJobCatalogTree, { NODE_SIZE } from 'modules/job_catalog/hooks/useJobCatalogTree'
import { useZoomToNode } from 'modules/job_catalog/hooks/useZoomToNode'
import {
  HierarchyLink,
  JobCatalogNodeData,
  PointNode,
} from 'modules/job_catalog/pages/JobCatalogTreePage/types'
import linkGenerator from 'modules/org_chart/lib/linkGenerator'
import Frame from 'modules/people_chart/components/GroupChart/Frame'
import useZoom from 'modules/people_chart/hooks/useZoom'
import { Dimensions } from 'modules/people_chart/lib/Dimensions'

import ActionBar from '../ActionBar'
import JobCatalogNode from '../JobCatalogNode'
import LevelGroup from '../LevelGroup'

const MIN_ZOOM = 0.37
const MAX_ZOOM = 1

interface Props {
  size: Dimensions
  hierarchy: HierarchyLink[]
  nodeData: JobCatalogNodeData[]
  uncollapsedIds?: Set<string>
  onNodeSelection?: (nodeId: string) => void
  onCollapseChange?: (nodeId: string) => void
  zoomToNodeId?: string
}

const groupLevelsByRole = (nodes: JobCatalogNodeData[]) => {
  const groups = new Map<string, { roleNode: JobCatalogNodeData; levels: JobCatalogNodeData[] }>()
  nodes.forEach((node) => {
    if (node.type === 'level' && node.parentId) {
      const roleNode = nodes.find((n) => n.id === node.parentId)
      if (roleNode) {
        if (!groups.has(roleNode.id)) {
          groups.set(roleNode.id, { roleNode, levels: [] })
        }
        const group = groups.get(roleNode.id)
        if (group) {
          group.levels.push(node)
        }
      }
    }
  })
  return groups
}

const getVisibleRolesWithVisibleLevels = (
  tree: JobCatalogNodeData[],
  uncollapsedIds: Set<string>,
  levelGroups: Map<string, { roleNode: JobCatalogNodeData; levels: JobCatalogNodeData[] }>
): Set<string> => {
  const roleIds = new Set<string>()
  levelGroups.forEach((group, roleId) => {
    if (uncollapsedIds.has(roleId)) {
      const anyVisible = group.levels.some((level) => tree.some((node) => node.id === level.id))
      if (anyVisible) {
        roleIds.add(roleId)
      }
    }
  })
  return roleIds
}

const Tree: FC<Props> = ({
  size,
  hierarchy,
  nodeData,
  onNodeSelection,
  onCollapseChange,
  uncollapsedIds = new Set(),
  zoomToNodeId,
}) => {
  const svgRef = useRef<SVGSVGElement>(null)
  const [width, height] = size
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const { tree, links, treeBoundary } = useJobCatalogTree(
    hierarchy,
    width,
    nodeData,
    uncollapsedIds
  )
  const zoom = useZoom(svgRef, size)

  useUnsafeEffect(() => {
    if (svgRef.current) {
      if (zoom.isReady) {
        zoom.scale(0.8)
        zoom.fitToBoundingBox(treeBoundary)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoom.isReady])

  const zoomToNode = useUnsafeCallback(
    (node: PointNode<JobCatalogNodeData>) => {
      const padding = width * 0.3
      zoom.fitToBoundingBox({
        x0: node.x,
        x1: node.x + NODE_SIZE[0] + padding,
        y0: node.y,
        y1: node.y + NODE_SIZE[1] + padding,
      })
    },
    [width, zoom]
  )

  useZoomToNode({
    zoomToNodeId,
    zoom,
    tree,
    nodeData,
    uncollapsedIds,
    onCollapseChange,
    setSelectedNodeId,
    zoomToNode,
  })

  const levelGroups = useMemo(() => groupLevelsByRole(nodeData), [nodeData])
  const visibleRoleIds = useMemo(
    () =>
      getVisibleRolesWithVisibleLevels(
        tree.map((node) => node.data),
        uncollapsedIds,
        levelGroups
      ),
    [tree, uncollapsedIds, levelGroups]
  )

  const currentZoom = useMemo(() => {
    const match = zoom.value.match(/scale\(([\d.]+)\)/)
    const scale = match ? parseFloat(match[1] || '1') : 1
    return Math.round(((scale - MIN_ZOOM) * 75) / (MAX_ZOOM - MIN_ZOOM) + 25)
  }, [zoom.value])

  const treeLinks = useUnsafeMemo(
    () =>
      links
        .filter(
          (link) =>
            !(
              link.target.data.type === 'level' &&
              visibleRoleIds.has(link.target.data.parentId || '')
            )
        )
        .map((link, i) => {
          const [nodeWidth, nodeHeight] = NODE_SIZE
          const linkPath = linkGenerator({
            source: [link.source.x + nodeWidth / 2, link.source.y + nodeHeight],
            target: [link.target.x + nodeWidth / 2, link.target.y],
          })

          if (link.target.id?.includes('TreeLevel')) return null

          return (
            <JobCatalogNode.Link
              key={`link-${i}-${link.source.id}-to-${link.target.id}`}
              d={linkPath}
            />
          )
        }),
    [links, visibleRoleIds]
  )

  const treeNodes = useUnsafeMemo(() => {
    const filteredTree = tree.filter(
      (node) => !(node.data.type === 'level' && visibleRoleIds.has(node.data.parentId || ''))
    )

    return filteredTree.map((node) => {
      if (!node.id) return null
      const nodeId = node.id

      const handleNodeClick = () => {
        setSelectedNodeId(nodeId || null)
        onNodeSelection?.(nodeId)
        zoomToNode(node)
      }

      const handleCollapseChange = () => {
        onCollapseChange?.(nodeId)
      }

      const hasChildren = hierarchy.some((h) => h.parentId === nodeId)

      if (node.data.type === 'role' && visibleRoleIds.has(nodeId)) {
        const levels = levelGroups.get(nodeId)?.levels || []
        return (
          <LevelGroup
            key={`level-group-${nodeId}`}
            x={node.x}
            y={node.y}
            roleId={nodeId}
            roleName={node.data.name}
            levels={levels}
            onNodeSelection={onNodeSelection}
            onCollapseChange={handleCollapseChange}
            isUncollapsed={uncollapsedIds.has(nodeId)}
          />
        )
      }

      return (
        <JobCatalogNode
          key={`node-${nodeId}`}
          data={node.data}
          x={node.x}
          y={node.y}
          onClick={handleNodeClick}
          hasChildren={hasChildren}
          isUncollapsed={uncollapsedIds.has(nodeId)}
          onCollapseChange={handleCollapseChange}
          childrenCount={hierarchy.filter((h) => h.parentId === nodeId).length}
          isSelected={nodeId === selectedNodeId}
        />
      )
    })
  }, [
    tree,
    visibleRoleIds,
    hierarchy,
    uncollapsedIds,
    selectedNodeId,
    onNodeSelection,
    zoomToNode,
    onCollapseChange,
    levelGroups,
  ])

  const handleZoomToFit = () => {
    zoom.fitToBoundingBox(treeBoundary)
  }

  return (
    <Box position="relative" width="full" height="full" style={{ overflow: 'hidden' }}>
      <Frame ref={svgRef} width={width} height={height} transform={zoom.value} showPointer>
        {treeLinks}
        {treeNodes}
      </Frame>

      <Box position="absolute" style={{ top: '20px', left: '20px', zIndex: '1' }}>
        <Button
          onClick={handleZoomToFit}
          round
          variant="outline"
          size="md"
          hiddenLabel
          icon={Filter}
        />
      </Box>
      {!!height && (
        <Box
          position="absolute"
          style={{
            right: '50%',
            bottom: '60px',
            zIndex: '1',
          }}
          padding="s4"
        >
          <ActionBar onClick={zoom.scale} currentZoom={currentZoom} />
        </Box>
      )}
    </Box>
  )
}

export default Tree
